import { createRoot } from 'react-dom/client';
import { lazy } from 'react';
import {
  BrowserFieldBootstrap,
  type BrowserApplicationModule,
  type BrowserFieldReadiness,
} from './offline/BrowserFieldBootstrap';
import { registerBrowserFieldShell } from './offline/register-service-worker';

const loadOnlineApplication = async (): Promise<BrowserApplicationModule> => {
  const [application, retiredStorage] = await Promise.all([
    import('./App'),
    import('./utils/retired-intake-purge'),
  ]);
  let retiredPurgeCompletion:
    | ReturnType<typeof retiredStorage.startRetiredIntakePurge>
    | undefined;
  const startRetiredIntakePurge = () =>
    (retiredPurgeCompletion ??= retiredStorage.startRetiredIntakePurge());
  void startRetiredIntakePurge();
  await retiredPurgeCompletion;
  return { default: application.default };
};

const OnlineApplication = lazy(loadOnlineApplication);

/**
 * Uses the portal's existing Cognito session restoration and authenticated
 * GET /me capability path. A browser connectivity bit never authorizes loading
 * the governed application. With no retained Cognito principal, only the
 * ordinary login surface may load; no governed session or API data exists.
 */
export const establishOnlinePortalReadiness: BrowserFieldReadiness = async (signal) => {
  const auth = await import('./providers/cognito');
  if (signal.aborted) throw new DOMException('Readiness cancelled', 'AbortError');
  if (auth.hasNoRetainedPortalSession()) return;
  await auth.checkCredentials();
  if (signal.aborted) throw new DOMException('Readiness cancelled', 'AbortError');
  await auth.probeOnlinePortalReadiness(signal);
  if (signal.aborted) throw new DOMException('Readiness cancelled', 'AbortError');
};

export const App = () => (
  <BrowserFieldBootstrap
    Application={OnlineApplication}
    establishOnlineReadiness={establishOnlinePortalReadiness}
  />
);

const root = createRoot(document.getElementById('root')!);
root.render(<App />);

if (import.meta.env.PROD) {
  window.addEventListener(
    'load',
    () => {
      void registerBrowserFieldShell().then((result) => {
        if (result.state === 'error')
          console.warn('The CHAMBER static shell could not be installed.');
      });
    },
    { once: true },
  );
}
